import React, { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, AlertCircle } from "lucide-react";

export const Card = ({ children, style = {} }) => (
  <div style={{ background: "white", borderRadius: 14, border: "1px solid #F1F5F9", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: 20, ...style }}>{children}</div>
);

export const PageHeader = ({ title, subtitle, action }) => (
  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>{title}</h1>
      {subtitle && <p style={{ color: "#6B7280", fontSize: 13, margin: "4px 0 0" }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Btn = ({ children, onClick, variant = "primary", size = "md", icon, disabled, style: s = {} }) => {
  const sizes = { sm: { padding: "6px 12px", fontSize: 12 }, md: { padding: "9px 16px", fontSize: 13 }, lg: { padding: "11px 20px", fontSize: 14 } };
  const variants = {
    primary: { background: "#2563EB", color: "white" },
    secondary: { background: "#F3F4F6", color: "#374151" },
    danger: { background: "#EF4444", color: "white" },
    success: { background: "#10B981", color: "white" },
    warning: { background: "#F59E0B", color: "white" },
    outline: { background: "transparent", color: "#2563EB", border: "1.5px solid #2563EB" },
    ghost: { background: "transparent", color: "#6B7280", border: "1.5px solid #E5E7EB" },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer", fontWeight: 600, transition: "all 0.15s", fontFamily: "inherit", ...sizes[size], ...variants[variant], opacity: disabled ? 0.45 : 1, ...s }}>
      {icon}{children}
    </button>
  );
};

export const Input = ({ value, onChange, placeholder, type = "text", style: s = {} }) => (
  <input value={value} onChange={onChange} type={type} placeholder={placeholder} style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E5E7EB", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", ...s }} onFocus={e => e.target.style.borderColor = "#2563EB"} onBlur={e => e.target.style.borderColor = "#E5E7EB"} />
);

export const Select = ({ value, onChange, options, placeholder, style: s = {} }) => (
  <select value={value} onChange={onChange} style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E5E7EB", borderRadius: 8, fontSize: 13, outline: "none", background: "white", cursor: "pointer", fontFamily: "inherit", ...s }}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const Field = ({ label, children, required }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{label}{required && <span style={{ color: "#EF4444", marginLeft: 2 }}>*</span>}</label>
    {children}
  </div>
);

export const Modal = ({ title, children, onClose, width = 560 }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
    <div style={{ background: "white", borderRadius: 16, width: "100%", maxWidth: width, maxHeight: "90vh", overflow: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}>
      <div style={{ padding: "20px 24px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>{title}</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }}><X size={20} /></button>
      </div>
      <div style={{ padding: 24 }}>{children}</div>
    </div>
  </div>
);

export const usePagination = (initialPageSize = 10) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const reset = useCallback(() => setPage(1), []);
  const paginate = useCallback((data) => {
    const total = data.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return { data: data.slice(start, start + pageSize), total, totalPages, page: safePage, from: total === 0 ? 0 : start + 1, to: Math.min(start + pageSize, total) };
  }, [page, pageSize]);
  return { page, setPage, pageSize, setPageSize, reset, paginate };
};

export const Pagination = ({ page, totalPages, total, from, to, pageSize, onPageChange, onPageSizeChange }) => {
  const pageSizeOpts = [5, 10, 20, 50];
  const getPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
    if (page >= totalPages - 3) return [1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "…", page - 1, page, page + 1, "…", totalPages];
  };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0 0", borderTop: "1px solid #F1F5F9", flexWrap: "wrap", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#6B7280" }}>{total === 0 ? "Tidak ada data" : `Menampilkan ${from}–${to} dari ${total} data`}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#9CA3AF" }}>Baris:</span>
          <select value={pageSize} onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }} style={{ padding: "4px 8px", border: "1.5px solid #E5E7EB", borderRadius: 6, fontSize: 12, cursor: "pointer", background: "white", outline: "none", fontFamily: "inherit" }}>
            {pageSizeOpts.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button onClick={() => onPageChange(1)} disabled={page === 1} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #E5E7EB", borderRadius: 7, background: "white", cursor: page === 1 ? "not-allowed" : "pointer", color: page === 1 ? "#D1D5DB" : "#6B7280", opacity: page === 1 ? 0.5 : 1 }}><ChevronsLeft size={13} /></button>
        <button onClick={() => onPageChange(page - 1)} disabled={page === 1} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #E5E7EB", borderRadius: 7, background: "white", cursor: page === 1 ? "not-allowed" : "pointer", color: page === 1 ? "#D1D5DB" : "#6B7280", opacity: page === 1 ? 0.5 : 1 }}><ChevronLeft size={13} /></button>
        {getPages().map((p, i) => p === "…" ? <span key={`e-${i}`} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#9CA3AF" }}>…</span> : <button key={p} onClick={() => onPageChange(p)} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", background: p === page ? "#2563EB" : "white", color: p === page ? "white" : "#374151", borderColor: p === page ? "#2563EB" : "#E5E7EB" }}>{p}</button>)}
        <button onClick={() => onPageChange(page + 1)} disabled={page === totalPages} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #E5E7EB", borderRadius: 7, background: "white", cursor: page === totalPages ? "not-allowed" : "pointer", color: page === totalPages ? "#D1D5DB" : "#6B7280", opacity: page === totalPages ? 0.5 : 1 }}><ChevronRight size={13} /></button>
        <button onClick={() => onPageChange(totalPages)} disabled={page === totalPages} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #E5E7EB", borderRadius: 7, background: "white", cursor: page === totalPages ? "not-allowed" : "pointer", color: page === totalPages ? "#D1D5DB" : "#6B7280", opacity: page === totalPages ? 0.5 : 1 }}><ChevronsRight size={13} /></button>
      </div>
    </div>
  );
};

export const Table = ({ cols, data, emptyMsg = "Tidak ada data", pageSize = 10 }) => {
  const pg = usePagination(pageSize);
  const { data: rows, total, totalPages, page, from, to } = pg.paginate(data);
  useEffect(() => { pg.reset(); }, [data.length]);
  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E5E7EB" }}>{cols.map((c, i) => <th key={i} style={{ padding: "10px 14px", textAlign: c.align || "left", fontWeight: 600, color: "#6B7280", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={cols.length} style={{ padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>{emptyMsg}</td></tr> : rows.map((row, ri) => <tr key={ri} style={{ borderBottom: "1px solid #F1F5F9" }}>{cols.map((c, ci) => <td key={ci} style={{ padding: "10px 14px", color: "#374151", textAlign: c.align || "left", whiteSpace: c.nowrap ? "nowrap" : "normal" }}>{c.render ? c.render(row[c.key], row) : row[c.key]}</td>)}</tr>)}
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pg.pageSize} onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} />
    </div>
  );
};

export const Spinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 48 }}>
    <div style={{ width: 32, height: 32, border: "3px solid #E5E7EB", borderTopColor: "#2563EB", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export const ErrBox = ({ msg, onRetry }) => (
  <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#DC2626", fontSize: 13 }}><AlertCircle size={16} /> {msg || "Gagal memuat data"}</div>
    {onRetry && <Btn size="sm" variant="outline" onClick={onRetry} icon={<RefreshCw size={12} />}>Coba lagi</Btn>}
  </div>
);